import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowUpRightIcon, CheckIcon, Loader2Icon } from 'lucide-react';
import { SubpageLayout } from '../components/SubpageLayout';
import { company } from '../data/legal';

// Set this to a form endpoint (e.g. Formspree, Basin or your own API) to deliver submissions.
// While empty, submissions are simulated so the flow can be reviewed.
const FORM_ENDPOINT = '';
const topics = [{
  value: 'studios',
  label: 'A project with LAEBEL Studios'
}, {
  value: 'equity',
  label: 'An equity partnership'
}, {
  value: 'labs',
  label: 'AI-OS with LAEBEL Labs'
}, {
  value: 'press',
  label: 'Press or speaking'
}, {
  value: 'other',
  label: 'Something else'
}];
const budgets = ['Under $50k', '$50k–150k', '$150k–500k', '$500k+', 'Equity only'];
type Fields = {
  name: string;
  email: string;
  company: string;
  topic: string;
  budget: string;
  message: string;
  consent: boolean;
};
type Errors = Partial<Record<keyof Fields, string>>;
type Status = 'idle' | 'submitting' | 'success' | 'error';
const ease = [0.23, 1, 0.32, 1] as const;
export function Contact() {
  const [params] = useSearchParams();
  const initialTopic = topics.some(t => t.value === params.get('topic')) ? params.get('topic') as string : 'studios';
  const [fields, setFields] = useState<Fields>({
    name: '',
    email: '',
    company: '',
    topic: initialTopic,
    budget: '',
    message: '',
    consent: false
  });
  const [errors, setErrors] = useState<Errors>({});
  const [status, setStatus] = useState<Status>('idle');
  const set = <K extends keyof Fields,>(key: K, value: Fields[K]) => {
    setFields(f => ({
      ...f,
      [key]: value
    }));
    if (errors[key]) setErrors(e => ({
      ...e,
      [key]: undefined
    }));
  };
  const validate = () => {
    const e: Errors = {};
    if (!fields.name.trim()) e.name = 'Please tell us your name.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fields.email)) e.email = 'Please enter a valid email address.';
    if (fields.message.trim().length < 10) e.message = 'A few words about what you are building, please.';
    if (!fields.consent) e.consent = 'Please confirm you have read the Privacy Policy.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };
  const onSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (status === 'submitting' || !validate()) return;
    setStatus('submitting');
    try {
      if (FORM_ENDPOINT) {
        const res = await fetch(FORM_ENDPOINT, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify(fields)
        });
        if (!res.ok) throw new Error('Request failed');
      } else {
        await new Promise(r => setTimeout(r, 900));
      }
      setStatus('success');
    } catch {
      setStatus('error');
    }
  };
  const reset = () => {
    setFields({
      name: '',
      email: '',
      company: '',
      topic: 'studios',
      budget: '',
      message: '',
      consent: false
    });
    setStatus('idle');
  };
  return <SubpageLayout title="Contact" data-id="element-458" data-mp="2w5d5lkln0dpa">
      <section className="px-page pb-20 pt-36 md:pb-28 md:pt-44" data-id="element-459" data-mp="2w6eogb8wa4hk">
        <div className="grid gap-14 md:grid-cols-12 md:gap-x-6" data-id="element-460" data-mp="2hgqq9iw0fly2">
          {/* Left: statement + direct details */}
          <div className="md:col-span-5" data-id="element-461" data-mp="2mes8k1lm3h8h">
            <p className="text-[12px] font-medium uppercase tracking-[0.04em] text-fg/60" data-id="element-462" data-mp="2mftiu3uszzp5">Contact</p>
            <h1 className="mt-4 font-serif text-[36px] font-normal leading-[1.08] tracking-[-0.01em] md:text-[56px]" data-id="element-463" data-mp="3a5xp6izdmdju">
              Tell us what you are <span className="text-fg/40" data-id="element-464" data-mp="314zg3ma2zfue">building</span>.
            </h1>
            <p className="mt-6 max-w-[420px] text-[15px] leading-[1.55] text-fg/55" data-id="element-465" data-mp="316smd0gs7y1v">
              Projects, equity partnerships or an AI-OS for your organisation — every enquiry is read by a partner, and we reply within one business day.
            </p>

            <dl className="mt-12 space-y-6 border-t border-line pt-6 text-[15px]" data-id="element-466" data-mp="1trnyvjbkqeqd">
              <div data-id="element-467" data-mp="29yaqa6aka716">
                <dt className="text-[13px] text-fg/45" data-id="element-468" data-mp="29zlqjw4sswnj">Email</dt>
                <dd className="mt-1" data-id="element-469" data-mp="256sl161i9kae">
                  <a href={`mailto:${company.email}`} className="text-fg transition-opacity duration-150 hover:opacity-70" data-id="element-470" data-mp="255pyfab7u40a">
                    {company.email}
                  </a>
                </dd>
              </div>
              <div data-id="element-471" data-mp="1v71t9c4bxje0">
                <dt className="text-[13px] text-fg/45" data-id="element-472" data-mp="2swmxhmhwx6u4">Phone</dt>
                <dd className="mt-1" data-id="element-473" data-mp="2sw3oiva8pss8">
                  <a href={`tel:${company.phoneHref}`} className="text-fg transition-opacity duration-150 hover:opacity-70" data-id="element-474" data-mp="2ny1yfqeect79">
                    {company.phoneDisplay}
                  </a>
                </dd>
              </div>
              <div data-id="element-475" data-mp="2ew2g8urnbj2q">
                <dt className="text-[13px] text-fg/45" data-id="element-476" data-mp="2ewkm8j441bnu">Headquarters</dt>
                <dd className="mt-1 max-w-[320px] leading-[1.45] text-fg/75" data-id="element-477" data-mp="17klhlzgxtxzm">{company.office}</dd>
              </div>
            </dl>
          </div>

          {/* Right: form / success */}
          <div className="md:col-span-6 md:col-start-7" data-id="element-478" data-mp="3f63fl3qmja6o">
            <AnimatePresence mode="wait" initial={false} data-id="element-479" data-mp="3f3h977fgz0q1">
              {status === 'success' ? <motion.div key="success" initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -8
            }} transition={{
              duration: 0.24,
              ease
            }} role="status" className="border-t border-line pt-8" data-id="element-480" data-mp="3j4j9zsp1bku4">
                  <span className="flex h-10 w-10 items-center justify-center rounded-control bg-fg text-bg" data-id="element-481" data-mp="6nxjgie0c0fq">
                    <CheckIcon className="h-5 w-5" aria-hidden="true" data-id="element-482" data-mp="arvikt3c1b8s" />
                  </span>
                  <h2 className="mt-6 font-serif text-[32px] font-normal leading-[1.1] tracking-[-0.01em] md:text-[40px]" data-id="element-483" data-mp="fpdveukh0am2">
                    Thank you, {fields.name.split(' ')[0]}.
                  </h2>
                  <p className="mt-4 max-w-[440px] text-[15px] leading-[1.55] text-fg/60" data-id="element-484" data-mp="2hhsa7krpwyy9">
                    Your message is with us. A partner will reply to {fields.email} within one business day.
                  </p>
                  <div className="mt-8 flex flex-wrap gap-3" data-id="element-485" data-mp="3tt4aat9pey29">
                    <Link to="/work" className="inline-flex h-11 items-center gap-2 rounded-control border border-fg/30 px-5 text-[13px] uppercase tracking-[0.04em] text-fg transition-colors duration-150 hover:border-fg hover:bg-fg hover:text-bg" data-id="element-486" data-mp="3tup3ofka8ayj">
                      See our work
                      <ArrowUpRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-487" data-mp="gdzk1pdfbhd4" />
                    </Link>
                    <button type="button" onClick={reset} className="h-11 px-2 text-[13px] text-fg/55 transition-colors duration-150 hover:text-fg" data-id="element-488" data-mp="cc4a5whffvnq">
                      Send another message
                    </button>
                  </div>
                </motion.div> : <motion.form key="form" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.2,
              ease
            }} onSubmit={onSubmit} noValidate className="border-t border-line" data-id="element-489" data-mp="19y74sjl59pm7">
                  <div className="grid gap-x-[var(--gutter)] md:grid-cols-2" data-id="element-490" data-mp="m7bejqwc72h2">
                    <Field label="Name" error={errors.name} htmlFor="name" data-id="element-491" data-mp="r04k2gzmqeu7">
                      <input id="name" autoComplete="name" value={fields.name} onChange={e => set('name', e.target.value)} aria-invalid={!!errors.name} className={inputClass} data-id="element-492" data-mp="cb86y5ipubmq" />
                    </Field>
                    <Field label="Email" error={errors.email} htmlFor="email" data-id="element-493" data-mp="nql6rmclgqj0">
                      <input id="email" type="email" autoComplete="email" value={fields.email} onChange={e => set('email', e.target.value)} aria-invalid={!!errors.email} className={inputClass} data-id="element-494" data-mp="noijha2ttbhz" />
                    </Field>
                  </div>

                  <Field label="Company (optional)" htmlFor="company" data-id="element-495" data-mp="10mn04pfn85">
                    <input id="company" autoComplete="organization" value={fields.company} onChange={e => set('company', e.target.value)} className={inputClass} data-id="element-496" data-mp="3r7l906jte6yu" />
                  </Field>

                  {/* Topic chips */}
                  <fieldset className="border-b border-line py-5" data-id="element-497" data-mp="3d8c628kmd9ct">
                    <legend className="text-[13px] text-fg/50" data-id="element-498" data-mp="1sqjg13qhphet">What is it about?</legend>
                    <div className="mt-3 flex flex-wrap gap-2" data-id="element-499" data-mp="1wqtvr8433m6v">
                      {topics.map(t => <Chip key={t.value} active={fields.topic === t.value} onClick={() => set('topic', t.value)} data-id="element-500" data-mp="21q6h1ofb4l7c">
                          {t.label}
                        </Chip>)}
                    </div>
                  </fieldset>

                  {fields.topic !== 'press' && <fieldset className="border-b border-line py-5" data-id="element-501" data-mp="1e1x0b0wyann2">
                      <legend className="text-[13px] text-fg/50" data-id="element-502" data-mp="1j088763oqo2n">Budget (optional)</legend>
                      <div className="mt-3 flex flex-wrap gap-2" data-id="element-503" data-mp="1nqewt5y768kw">
                        {budgets.map(b => <Chip key={b} active={fields.budget === b} onClick={() => set('budget', fields.budget === b ? '' : b)} data-id="element-504" data-mp="qbsz1078izz8">
                            {b}
                          </Chip>)}
                      </div>
                    </fieldset>}

                  <Field label="Message" error={errors.message} htmlFor="message" data-id="element-505" data-mp="tw5nzfde3gmm">
                    <textarea id="message" rows={5} value={fields.message} onChange={e => set('message', e.target.value)} aria-invalid={!!errors.message} placeholder="What are you building, and where do you stand today?" className={`${inputClass} resize-none placeholder:text-fg/30`} data-id="element-506" data-mp="yw1i8mwabtoz" />
                  </Field>

                  <div className="py-5" data-id="element-507" data-mp="g3ftemjsp3dw">
                    <label className="flex cursor-pointer items-start gap-3 text-[14px] leading-[1.5] text-fg/60" data-id="element-508" data-mp="kx25aci4x38n">
                      <input type="checkbox" checked={fields.consent} onChange={e => set('consent', e.target.checked)} aria-invalid={!!errors.consent} className="mt-1 h-4 w-4 shrink-0 accent-[#111111]" data-id="element-509" data-mp="3jl7ls21ufpcm" />
                      <span data-id="element-510" data-mp="1wpazvtd2icvb">
                        I have read the{' '}
                        <Link to="/legal/privacy" className="text-fg underline decoration-fg/30 underline-offset-4 hover:decoration-fg" data-id="element-511" data-mp="1wrdvcswzoy7f">
                          Privacy Policy
                        </Link>{' '}
                        and agree that LAEBEL may use my details to reply to this enquiry.
                      </span>
                    </label>
                    {errors.consent && <p className="mt-2 text-[13px] text-[#c2410c]" data-id="element-512" data-mp="1dzbaf5dbevrd">{errors.consent}</p>}
                  </div>

                  {status === 'error' && <p role="alert" className="mb-4 text-[14px] text-[#c2410c]" data-id="element-513" data-mp="1415c0xqpw7r9">
                      Something went wrong sending your message. Please try again, or email us at {company.email}.
                    </p>}

                  <button type="submit" disabled={status === 'submitting'} className="group inline-flex h-12 items-center gap-2 rounded-control bg-fg px-6 text-[13px] uppercase tracking-[0.04em] text-bg transition-opacity duration-150 hover:opacity-85 disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-fg focus-visible:ring-offset-2" data-id="element-514" data-mp="pcc0trsbrra1">
                    {status === 'submitting' ? <>
                        <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-515" data-mp="1d5lqf5iuicsj" />
                        Sending
                      </> : <>
                        Send message
                        <ArrowUpRightIcon className="h-4 w-4 transition-transform duration-150 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" aria-hidden="true" data-id="element-516" data-mp="1r2qtcwn9ivjk" />
                      </>}
                  </button>
                </motion.form>}
            </AnimatePresence>
          </div>
        </div>
      </section>
    </SubpageLayout>;
}
const inputClass = 'mt-1 block w-full bg-transparent py-1 text-[17px] text-fg outline-none placeholder:text-fg/30';
function Field({
  label,
  error,
  htmlFor,
  children
}: {
  label: string;
  error?: string;
  htmlFor: string;
  children: React.ReactNode;
}) {
  return <div className={`border-b py-5 transition-colors duration-150 focus-within:border-fg/60 ${error ? 'border-[#c2410c]' : 'border-line'}`} data-id="element-517" data-mp="2snqra8jynsot">
      <label htmlFor={htmlFor} className="text-[13px] text-fg/50" data-id="element-518" data-mp="37hd649vv3w1l">
        {label}
      </label>
      {children}
      {error && <p className="mt-2 text-[13px] text-[#c2410c]" data-id="element-519" data-mp="mt8pbwgombbi">{error}</p>}
    </div>;
}
function Chip({
  active,
  onClick,
  children
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return <button type="button" aria-pressed={active} onClick={onClick} className={`whitespace-nowrap rounded-control border px-3.5 py-2 text-[14px] transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-fg ${active ? 'border-fg bg-fg text-bg' : 'border-line text-fg/70 hover:border-fg/30 hover:text-fg'}`} data-id="element-520" data-mp="2nj7qich98ayr">
      {children}
    </button>;
}